import { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import OtpInput from "react-otp-input";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Spinner } from "react-bootstrap";
import toast from "react-hot-toast";
import {
  otpLogin,
  resendLogin,
  saveTokenInLocalStorage,
} from "../../services/Auth/AuthService";
import { loginConfirmedAction } from "../../store/actions/AuthActions";
import logo from "../../images/logo.svg";

export default function OtpLogin() {
  const [timeLeft, setTimeLeft] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);

  const tokens = localStorage.getItem("tempToken");
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (!tokens) {
      navigate("/login", { replace: true });
    }
  }, [tokens, navigate]);

  useEffect(() => {
    if (timeLeft === 0) {
      setCanResend(true);
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  const formik = useFormik({
    initialValues: {
      otp: "",
    },
    validationSchema: Yup.object({
      otp: Yup.string()
        .required("OTP is required")
        .length(4, "OTP must be 4 digits"),
    }),
    onSubmit: async (values) => {
      try {
        setSubmitting(true);
        await otpLogin(
          { otp: values.otp },
          {
            headers: {
              Authorization: `Bearer ${tokens}`,
              Accept: "application/json",
            },
          }
        );

        saveTokenInLocalStorage(tokens);
        dispatch(loginConfirmedAction(tokens));
        localStorage.removeItem("tempToken");
        navigate("/dashboard");
      } catch (error) {
        toast.error(error?.response?.data?.message || "Invalid OTP");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleResend = async () => {
    const token = localStorage.getItem("tempToken");
    setTimeLeft(30);
    setCanResend(false);
    formik.resetForm();

    try {
      setResending(true);
      await resendLogin(
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );
      toast.success("OTP resent successfully");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to resend OTP");
      setCanResend(true);
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="qx-auth-page">
      <div className="qx-auth-shell">
        <aside className="qx-auth-brand">
          <div className="qx-auth-brand__inner">
            <Link to="/login" className="qx-auth-brand__logo">
              <img src={logo} alt="Manage My Shaadi" />
            </Link>
            <p className="qx-auth-brand__eyebrow">Admin Portal</p>
            <p className="qx-auth-brand__desc">
              Enter the 4-digit code we sent to your phone to continue securely.
            </p>
          </div>
        </aside>

        <main className="qx-auth-panel">
          <div className="qx-auth-card">
            <div className="qx-auth-card__header">
              <h2 className="qx-auth-card__title">OTP Verification</h2>
              <p className="qx-auth-card__subtitle">
                Enter the OTP sent to your mobile number
              </p>
            </div>

            <form onSubmit={formik.handleSubmit} noValidate>
              <div className="qx-otp-wrap">
                <OtpInput
                  value={formik.values.otp}
                  onChange={(value) => formik.setFieldValue("otp", value)}
                  numInputs={4}
                  shouldAutoFocus
                  inputType="tel"
                  renderInput={(props) => (
                    <input {...props} className="qx-otp-input qx-keep" />
                  )}
                  containerStyle={{
                    display: "flex",
                    gap: "12px",
                    justifyContent: "center",
                  }}
                />
              </div>

              {formik.touched.otp && formik.errors.otp ? (
                <div className="text-danger text-center mb-3 fs-12">
                  {formik.errors.otp}
                </div>
              ) : null}

              <button
                type="submit"
                className="btn btn-primary qx-btn-brand qx-auth-submit"
                disabled={
                  submitting ||
                  !formik.isValid ||
                  formik.values.otp.length !== 4
                }
              >
                {submitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Verifying…
                  </>
                ) : (
                  "Verify OTP"
                )}
              </button>
            </form>

            <div className="qx-auth-footer">
              {canResend ? (
                <button
                  type="button"
                  className="qx-auth-link"
                  onClick={handleResend}
                  disabled={resending}
                >
                  {resending ? "Resending…" : "Resend OTP"}
                </button>
              ) : (
                <span className="qx-auth-muted">
                  Resend OTP in <strong>{timeLeft}s</strong>
                </span>
              )}

              <button
                type="button"
                className="qx-auth-link qx-auth-link--muted"
                onClick={() => navigate("/login")}
              >
                Change phone number
              </button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
